import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart3, 
  Users, 
  FileText, 
  Image, 
  ArrowUpRight, 
  HelpCircle, 
  Video, 
  Sparkles,
  TrendingUp,
  Activity,
  Zap,
  Eye,
  Plus,
  Upload,
  UserPlus,
  Mail,
  X,
  Box,
  Paintbrush
} from 'lucide-react';
import CoreMetricsCard from '../components/CoreMetricsCard';
import AudienceAnalyticsCard from '../components/AudienceAnalyticsCard';
import PlatformMetricsCards from '../components/PlatformMetricsCards';
import HealthScoreDashboard from '../components/analytics/HealthScoreDashboard';
import { useClient } from '../contexts/ClientContext';
import ClientService from '../services/clientService';
import MediaService from '../services/mediaService';
import PostService from '../services/postService';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import SocialConnect from '../components/social/SocialConnect';
import { formatTimeAgo } from '../utils/dateFormatter';
import { useSocialConnections } from '../hooks/useSocialConnections';
import DateRangePicker from '../components/dashboard/DateRangePicker';
import RecentComments from '../components/dashboard/RecentComments';
import InstagramRequirementsModal from '../components/modals/InstagramRequirementsModal';
import AnalyticsDebug from '../components/debug/AnalyticsDebug';
import CommentsDebug from '../components/debug/CommentsDebug';
import { useTour } from '../contexts/TourContext';
import { dashboardSteps, onboardingSteps } from '../components/onboarding/tour-steps';
import { useMobile } from '../hooks/useMobile';
import { Button } from '../components/ui/button';
import { getApiUrl } from '../utils/config';
import { motion } from 'framer-motion';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { useUserTier } from '../hooks/useUserTier';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import AuthenticatedImage from '../components/media/AuthenticatedImage';
import { InviteUserModal } from '../components/team/InviteUserModal';
import DashboardComingSoon from '../components/dashboard/DashboardComingSoon';
import { useAuth } from '../contexts/AuthContext';

const StatCard = ({ icon: Icon, name, value, change, color, className = '' }) => (
  <motion.div 
    className={`relative overflow-hidden rounded-2xl bg-white/90 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/60 dark:border-slate-600/40 transition-all duration-300 group ${className}`}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6 }}
    whileHover={{ scale: 1.03, y: -8 }}
  >
    <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/30 dark:from-blue-500/5 dark:via-purple-500/5 dark:to-slate-500/5"></div>
    <div className="relative p-3 sm:p-4 lg:p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2 sm:space-x-3 lg:space-x-4">
          <div className="relative">
            <div className="relative p-2 sm:p-2.5 lg:p-3 bg-gradient-to-br from-slate-100 to-slate-200/80 dark:from-slate-700/60 dark:to-slate-600/60 backdrop-blur-sm rounded-lg sm:rounded-xl border-0">
              <Icon className={`h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6 ${color}`} />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-400 uppercase tracking-wider truncate">{name}</p>
            <p className="text-xl sm:text-2xl lg:text-3xl font-black !text-black dark:!text-white mt-1 transition-colors hover:!text-[#30b3dd]">{value}</p>
          </div>
        </div>
      </div>
    </div>
    <div className="absolute bottom-0 left-0 right-0 h-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)' }}></div>
  </motion.div>
);

const QuickActionButton = ({ icon: Icon, title, subtitle, onClick, className = '', id }) => (
  <motion.button
    id={id}
    onClick={onClick}
    className={`relative overflow-hidden p-2 sm:p-3 text-left bg-white/60 dark:bg-slate-700/40 backdrop-blur-sm rounded-lg sm:rounded-xl border border-slate-200/40 dark:border-slate-600/30 hover:bg-white/80 dark:hover:bg-slate-700/60 transition-all duration-200 group w-full h-full ${className}`}
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
  >
    <div className="flex flex-col items-center justify-center h-full text-center px-1 py-1">
      <div className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 bg-slate-100 dark:bg-slate-600 rounded-lg flex items-center justify-center mb-1 sm:mb-1.5 transition-all duration-200 group-hover:bg-slate-200 dark:group-hover:bg-slate-500 flex-shrink-0">
        <Icon className="h-3 w-3 sm:h-3.5 sm:w-3.5 lg:h-4 lg:w-4 text-slate-600 dark:text-slate-300 transition-colors" />
      </div>
      <div className="flex-1 flex flex-col justify-center min-w-0 w-full">
        <p className="text-xs sm:text-sm font-semibold text-slate-800 dark:text-slate-200 transition-colors leading-tight mb-0.5 truncate w-full" title={title}>{title}</p>
        <p className="text-xs text-slate-600 dark:text-slate-400 transition-colors leading-tight hidden sm:block truncate w-full" title={subtitle}>{subtitle}</p>
      </div>
    </div>
  </motion.button>
);

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentClient } = useClient();
  const { currentTier } = useUserTier();
  const { startTour, restartTour } = useTour();
  const isMobile = useMobile();
  const { user } = useAuth();

  // Check if current user is a beta tester
  const isBetaTester = user?.is_beta_tester || false;

  const [email, setEmail] = useState('');
  const [selectedMedia, setSelectedMedia] = useState<any>(null);
  const [showMediaPreview, setShowMediaPreview] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);

  const queryClient = useQueryClient();
  const { 
    fetchConnections, 
    showInstagramModal, 
    setShowInstagramModal, 
    proceedWithInstagramConnection 
  } = useSocialConnections();
  const { flags } = useFeatureFlags();

  useEffect(() => {
    setTimeout(() => startTour(onboardingSteps, 'onboarding'), 1000);
  }, [startTour]);





  const { data: clientsData } = useQuery({
    queryKey: ['clients', currentClient?.id],
    queryFn: () => ClientService.getClients(),
    enabled: !!currentClient?.id,
    staleTime: 0, // Prevent cross-user data leaks
  });

  const { data: mediaData } = useQuery({
    queryKey: ['mediaFiles', currentClient?.id],
    queryFn: () => MediaService.getMediaFiles(1, 1), // Let API service handle client ID automatically
    enabled: !!currentClient?.id,
    staleTime: 0, // Prevent cross-user data leaks
  });

  // Fetch more media files to get accurate type counts
  const { data: allMediaForCounting } = useQuery({
    queryKey: ['allMediaForCounting', currentClient?.id],
    queryFn: () => MediaService.getMediaFiles(1, 500), // Let API service handle client ID automatically
    enabled: !!currentClient?.id && (mediaData?.total || 0) > 0,
    staleTime: 0,
  });

  // Since /media/stats is failing but basic /media works, let's count types from a sample
  const { data: mediaSampleData } = useQuery({
    queryKey: ['mediaSample', currentClient?.id],
    queryFn: () => MediaService.getMediaFiles(1, 100), // Get first 100 files to analyze types
    enabled: !!currentClient?.id && (mediaData?.total || 0) > 0,
    staleTime: 0,
  });

  // Calculate stats from the sample
  const mediaStatsData = React.useMemo(() => {
    const totalFiles = mediaData?.total || 0;
    
    if (!mediaSampleData?.files || mediaSampleData.files.length === 0) {
      return { total_files: totalFiles, image_count: 0, video_count: 0 };
    }

    // Count types in the sample
    const sampleFiles = mediaSampleData.files;
    const imageCount = sampleFiles.filter(file => 
      file.media_type === 'image' || file.mime_type?.startsWith('image/')
    ).length;
    
    const videoCount = sampleFiles.filter(file => 
      file.media_type === 'video' || file.mime_type?.startsWith('video/')
    ).length;

    const modelCount = sampleFiles.filter(file => 
      file.mime_type === 'model/gltf-binary' || 
      file.mime_type?.startsWith('model/') ||
      (file.media_type === 'other' && file.original_filename?.match(/\.(glb|gltf|obj|fbx|dae|3ds|blend|usd|usdz)$/i))
    ).length;

    // Extrapolate to total based on sample ratio
    const sampleSize = sampleFiles.length;
    const estimatedImages = Math.round((imageCount / sampleSize) * totalFiles);
    const estimatedVideos = Math.round((videoCount / sampleSize) * totalFiles);
    const estimatedModels = Math.round((modelCount / sampleSize) * totalFiles);

    console.log(`Sample analysis: ${imageCount}/${sampleSize} images, ${videoCount}/${sampleSize} videos, ${modelCount}/${sampleSize} 3D models`);
    console.log(`Estimated totals: ${estimatedImages} images, ${estimatedVideos} videos, ${estimatedModels} 3D models out of ${totalFiles}`);

    return {
      total_files: totalFiles,
      image_count: estimatedImages,
      video_count: estimatedVideos,
      model_count: estimatedModels,
    };
  }, [mediaData, mediaSampleData]);

  const mediaStatsError = null;



  const { data: postsData } = useQuery({
    queryKey: ['posts', 'dashboard', currentClient?.id],
    queryFn: () => PostService.getPosts(1, 1),
    enabled: !!currentClient?.id,
    staleTime: 0, // Prevent cross-user data leaks
  });



  const [{ data: recentPostsData }, { data: recentMediaData }] = useQueries({
    queries: [
      {
        queryKey: ['recentPosts', currentClient?.id],
        queryFn: () => PostService.getPosts(1, 5),
        enabled: !!currentClient?.id,
        staleTime: 0, // Prevent cross-user data leaks
      },
      {
        queryKey: ['recentMedia', currentClient?.id],
        queryFn: () => MediaService.getMediaFiles(1, 5),
        enabled: !!currentClient?.id,
        staleTime: 0, // Prevent cross-user data leaks
      },
    ],
  });

  const activeClientsCount = clientsData?.clients.length || 0;
  const mediaFilesCount = mediaData?.total || 0;
  const totalPostsCount = postsData?.total || 0;
  const imageFilesCount = mediaStatsData?.image_count || 0;
  const videoFilesCount = mediaStatsData?.video_count || 0;
  const modelFilesCount = mediaStatsData?.model_count || 0;

  // Debug logging for media stats issues
  if (mediaStatsError) {
    console.error('Media stats error:', mediaStatsError);
  }
  if (mediaFilesCount > 0 && imageFilesCount === 0 && videoFilesCount === 0) {
    console.warn('Media files exist but image/video counts are zero. Check media_type values in database.');
  }

  const recentActivities = React.useMemo(() => {
    const activities: { type: string; title: string; date: string; icon: React.ElementType, link: string, mediaData?: any }[] = [];

    recentPostsData?.posts.forEach(post => {
      activities.push({
        type: 'post',
        title: `New post: ${post.title}`,
        date: post.created_at,
        icon: FileText,
        link: `/app/posts/${post.id}`
      });
    });

    recentMediaData?.files.forEach(media => {
      activities.push({
        type: 'media',
        title: `New media: ${media.original_filename}`,
        date: media.created_at,
        icon: Image,
        link: `/app/media`,
        mediaData: media
      });
    });

    // Sort by date descending
    activities.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    return activities.slice(0, 5);
  }, [recentPostsData, recentMediaData]);

  const handleActivityClick = (activity: any) => {
    if (activity.type === 'media' && activity.mediaData) {
      setSelectedMedia(activity.mediaData);
      setShowMediaPreview(true);
    } else {
      navigate(activity.link);
    }
  };

  const handleViewClick = (e: React.MouseEvent, activity: any) => {
    e.stopPropagation();
    if (activity.type === 'media' && activity.mediaData) {
      setSelectedMedia(activity.mediaData);
      setShowMediaPreview(true);
    } else {
      navigate(activity.link);
    }
  };

  const stats = [
    // { name: 'Total Posts', value: totalPostsCount.toString(), icon: FileText, change: '+12%', color: 'text-indigo-500' }, // Hidden
    { name: 'Active Brands', value: activeClientsCount.toString(), icon: Users, change: '+2', color: 'text-emerald-500' },
    { name: 'Media Files', value: mediaFilesCount.toString(), icon: Image, change: '+18%', color: 'text-amber-500' },
    { name: 'Images', value: imageFilesCount.toString(), icon: Image, change: imageFilesCount > 0 ? 'Ready' : 'Upload', color: 'text-rose-500' },
    { name: '3D Models', value: modelFilesCount.toString(), icon: Box, change: modelFilesCount > 0 ? 'Ready' : 'Upload', color: 'text-purple-500' },
    { name: 'Videos', value: videoFilesCount.toString(), icon: Video, change: videoFilesCount > 0 ? 'Ready' : 'Upload', color: 'text-cyan-500' },
  ];

  const quickActions = [
    { id: 'invite-member-button', icon: UserPlus, title: 'Invite Member', subtitle: 'Add team member', onClick: () => setShowInviteModal(true) },
    { icon: Upload, title: 'Upload Media', subtitle: 'Add files', onClick: () => navigate('/app/media') },
    { icon: Paintbrush, title: 'Brand Kit', subtitle: 'Manage brand', onClick: () => navigate('/app/clients') },
    { icon: Zap, title: 'AI Creator', subtitle: 'AI Campaign', onClick: () => navigate('/app/one-click-creator') },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-900 dark:via-slate-900 dark:to-gray-900 text-gray-900 dark:text-white">
      {/* Page Header */}
      <div className="backdrop-blur-2xl bg-white/60 dark:bg-slate-900/60 border-b border-slate-200/50 dark:border-slate-700/30">
        <div className="px-2 sm:px-4 lg:px-8 py-3 sm:py-4 lg:py-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="flex items-center space-x-2 sm:space-x-3 lg:space-x-4"
            >
              <div className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-lg sm:rounded-xl lg:rounded-2xl flex items-center justify-center shadow-lg" style={{ background: 'linear-gradient(135deg, #30b3dd, #4a298a)', boxShadow: '0 10px 25px rgba(48, 179, 221, 0.25)' }}>
                <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div>
                <h1 id="dashboard-welcome" className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-primary via-blue-600 to-cyan-600 bg-clip-text text-transparent">
                  Dashboard
                </h1>
                <p className="text-sm sm:text-base text-muted-foreground">Your social media command center</p>
              </div>
            </motion.div>
            
            {!isMobile && (
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="w-full sm:w-auto"
              >
                <Button 
                  onClick={() => restartTour(onboardingSteps, 'onboarding')} 
                  variant="outline" 
                  size="sm"
                  className="text-slate-600 border-slate-300/50 bg-white/50 hover:bg-slate-100/50 backdrop-blur-sm shadow-lg shadow-slate-200/50 dark:text-slate-300 dark:border-slate-600/50 dark:bg-transparent dark:hover:bg-slate-800/30 dark:shadow-none"
                >
                  <HelpCircle className="mr-2 h-4 w-4" />
                  Take a tour
                </Button>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-2 sm:px-4 lg:px-8 py-2 sm:py-4 lg:py-8">


        <motion.div 
          className="grid grid-cols-1 gap-3 sm:gap-6 lg:gap-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          {/* Stats Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 lg:gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="min-w-0" // Ensure proper flex behavior
              >
                <StatCard {...stat} />
              </motion.div>
            ))}
          </div>



          {/* Main Dashboard Grid */}
          <div className="space-y-3 sm:space-y-6 lg:space-y-8">
            {/* Top Row - Recent Activity and Comments */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-6 lg:gap-8">
              {/* Recent Activity */}
              <motion.div 
                id="recent-posts-widget" 
                className="lg:col-span-2 relative overflow-hidden rounded-lg sm:rounded-xl lg:rounded-2xl bg-white/90 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/60 dark:border-slate-600/40 shadow-xl shadow-slate-200/60 dark:shadow-xl h-[300px] sm:h-[350px] lg:h-[400px] flex flex-col"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
              >
            <div className="absolute inset-0 bg-gradient-to-br from-slate-50/50 via-blue-50/30 to-purple-50/30 dark:from-blue-500/5 dark:via-purple-500/5 dark:to-slate-500/5"></div>
            
              {/* Header */}
              <div className="relative border-b border-slate-200/60 dark:border-slate-600/40 p-3 sm:p-4 lg:p-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-3">
                  <div className="flex items-center space-x-2 sm:space-x-3 lg:space-x-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                      <Activity className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <h2 className="text-lg sm:text-xl font-bold text-slate-800 dark:text-slate-200">Recent Activity</h2>
                  </div>
                  <div className="flex items-center space-x-2 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-1 rounded-full">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                    <span className="text-xs sm:text-sm text-emerald-700 dark:text-emerald-400 font-medium">Live</span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="relative p-2 sm:p-3 lg:p-4 space-y-2 sm:space-y-3 overflow-y-auto flex-grow">
                {recentActivities.length === 0 ? (
                  <div className="text-center py-6 sm:py-8">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-slate-100 to-slate-200/80 dark:from-slate-700/60 dark:to-slate-600/60 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Activity className="w-5 h-5 sm:w-6 sm:h-6 text-slate-600 dark:text-slate-400" />
                    </div>
                    <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 font-medium">No recent activity to show.</p>
                  </div>
                ) : (
                  recentActivities.map((activity, index) => (
                    <motion.div 
                      key={index} 
                      className="flex items-center justify-between gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg sm:rounded-xl bg-slate-50/50 dark:bg-slate-700/40 backdrop-blur-sm border border-slate-200/60 dark:border-slate-600/40 hover:bg-slate-100/50 dark:hover:bg-slate-600/50 transition-all duration-300 cursor-pointer group" 
                      onClick={() => handleActivityClick(activity)}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      whileHover={{ scale: 1.01, x: 2 }}
                    >
                      <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
                        <div className="w-8 h-8 sm:w-9 sm:h-9 bg-slate-100 dark:bg-slate-600 rounded-lg flex items-center justify-center group-hover:bg-slate-200 dark:group-hover:bg-slate-500 transition-all duration-300 flex-shrink-0 overflow-hidden">
                          {activity.type === 'media' && activity.mediaData?.thumbnail_url ? (
                            <AuthenticatedImage
                              src={activity.mediaData.thumbnail_url || activity.mediaData.file_url}
                              alt={activity.mediaData.original_filename}
                              className="w-full h-full object-cover rounded-lg"
                              clientId={currentClient?.id}
                              onError={() => {
                                // Fallback to icon if image fails to load
                                console.log('Thumbnail failed to load, showing fallback icon');
                              }}
                            />
                          ) : (
                            <activity.icon className="h-3 w-3 sm:h-4 sm:w-4" style={{ color: activity.type === 'post' ? '#30b3dd' : '#4a298a' }} />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-xs sm:text-sm font-semibold text-slate-800 dark:text-slate-200 transition-colors block truncate">{activity.title}</span>
                          <p className="text-xs text-slate-600 dark:text-slate-400 font-medium">{formatTimeAgo(activity.date)}</p>
                        </div>
                      </div>
                      <Button 
                        onClick={(e) => handleViewClick(e, activity)} 
                        size="sm"
                        className="text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs font-semibold transition-all duration-300 flex-shrink-0"
                        style={{ 
                          background: 'linear-gradient(to right, #2891b8, #3a1f6b)',
                          backgroundSize: '200% 100%',
                          backgroundPosition: 'left center'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundPosition = 'right center';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundPosition = 'left center';
                        }}
                      >
                        <Eye className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                        <span className="hidden sm:inline">View</span>
                      </Button>
                    </motion.div>
                  ))
                )}
              </div>
            </motion.div>





              {/* Quick Actions */}
              <motion.div 
                id="scheduled-posts-widget" 
                className="lg:col-span-1 relative overflow-hidden rounded-lg sm:rounded-xl lg:rounded-2xl bg-white/90 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/60 dark:border-slate-600/40 shadow-xl shadow-slate-200/60 dark:shadow-xl h-[300px] sm:h-[350px] lg:h-[400px] flex flex-col"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.7 }}
              >
              <div className="absolute inset-0 bg-gradient-to-br from-slate-50/50 via-blue-50/30 to-purple-50/30 dark:from-blue-500/5 dark:via-purple-500/5 dark:to-slate-500/5"></div>
              
              {/* Header */}
              <div className="relative border-b border-slate-200/60 dark:border-slate-600/40 p-3 sm:p-4 lg:p-6">
                <div className="flex items-center space-x-2 sm:space-x-3 lg:space-x-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-lg sm:rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #30b3dd, #4a298a)' }}>
                    <Zap className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" />
                  </div>
                  <h2 className="text-base sm:text-lg lg:text-xl font-bold text-slate-800 dark:text-slate-200">Quick Actions</h2>
                </div>
              </div>

              {/* Actions Grid */}
              <div className="relative p-2 sm:p-3 lg:p-4 grid grid-cols-2 gap-2 sm:gap-3 flex-grow">
                {quickActions.map((action, index) => (
                  <motion.div
                    key={action.title}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4, delay: 0.8 + index * 0.1 }}
                    className="min-w-0 h-[80px] sm:h-[90px] lg:h-[110px]"
                  >
                    <QuickActionButton {...action} id={action.id} />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
          </div>

        </motion.div>

        {/* Analytics Section - Recent Comments + Social Analytics */}
        <div className="relative mt-6 lg:mt-8">
          <div className={`relative space-y-6 lg:space-y-8 ${!flags.social_analytics && !isBetaTester ? 'filter blur-sm pointer-events-none' : ''}`}>
              {/* Recent Comments Section */}
              <motion.div 
                id="recent-comments-widget" 
                className="relative overflow-hidden rounded-xl sm:rounded-2xl bg-white/90 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/60 dark:border-slate-600/40 shadow-xl shadow-slate-200/60 dark:shadow-xl h-[350px] sm:h-[400px] flex flex-col"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-slate-50/50 via-blue-50/30 to-purple-50/30 dark:from-blue-500/5 dark:via-purple-500/5 dark:to-slate-500/5"></div>
                <div className="relative p-4 sm:p-6 h-full">
                  <RecentComments />
                </div>
              </motion.div>

              {/* Social Analytics Grid */}
              <div className="grid grid-cols-1 gap-6 lg:gap-8">
              {/* Social Media Metrics */}
              <motion.div
                id="social-media-metrics-widget"
                className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
              >
                <div className="relative overflow-hidden rounded-xl sm:rounded-2xl bg-white/90 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/60 dark:border-slate-600/40 shadow-xl shadow-slate-200/60 dark:shadow-xl">
                  <div className="absolute inset-0 bg-gradient-to-br from-slate-50/50 via-blue-50/30 to-purple-50/30 dark:from-blue-500/5 dark:via-purple-500/5 dark:to-slate-500/5"></div>
                  <div className="relative">
                    <CoreMetricsCard />
                  </div>
                </div>

                <div className="relative overflow-hidden rounded-xl sm:rounded-2xl bg-white/90 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/60 dark:border-slate-600/40 shadow-xl shadow-slate-200/60 dark:shadow-xl">
                  <div className="absolute inset-0 bg-gradient-to-br from-slate-50/50 via-blue-50/30 to-purple-50/30 dark:from-blue-500/5 dark:via-purple-500/5 dark:to-slate-500/5"></div>
                  <div className="relative">
                    <AudienceAnalyticsCard />
                  </div>
                </div>
              </motion.div>

              {/* Health Score Dashboard */}
              <motion.div
                className="relative overflow-hidden rounded-xl sm:rounded-2xl bg-white/90 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/60 dark:border-slate-600/40 shadow-xl shadow-slate-200/60 dark:shadow-xl"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.85 }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-slate-50/50 via-blue-50/30 to-purple-50/30 dark:from-blue-500/5 dark:via-purple-500/5 dark:to-slate-500/5"></div>
                <div className="relative">
                  <HealthScoreDashboard />
                </div>
              </motion.div>

              {/* Platform Metrics */}
              <motion.div
                id="platform-metrics-section"
                className="relative overflow-hidden rounded-xl sm:rounded-2xl bg-white/90 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/60 dark:border-slate-600/40 shadow-xl shadow-slate-200/60 dark:shadow-xl"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.9 }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-slate-50/50 via-blue-50/30 to-purple-50/30 dark:from-blue-500/5 dark:via-purple-500/5 dark:to-slate-500/5"></div>
                <div className="relative">
                  <PlatformMetricsCards />
                </div>
              </motion.div>

              </div>
            </div>
            
            {!flags.social_analytics && !isBetaTester && (
              <DashboardComingSoon
                feature="social_analytics"
                title="Social Analytics Coming Soon!"
                description="Unlock powerful insights into your social media performance."
                features={[
                  "Audience growth and demographics",
                  "Content performance analysis",
                  "Sentiment analysis",
                  "Social media health score",
                  "Engagement history and follower growth"
                ]}

              />
            )}
        </div>

      </div>

      {/* Debug Components - Hidden for production */}
      {/* <AnalyticsDebug /> */}
      {/* <CommentsDebug /> */}

      {/* Media Preview Modal */}
      <Dialog open={showMediaPreview} onOpenChange={setShowMediaPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              {selectedMedia?.original_filename}
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-4">
            {selectedMedia && (
              <div className="max-w-full max-h-[70vh] overflow-hidden rounded-lg">
                {selectedMedia.mime_type?.startsWith('image') ? (
                  <AuthenticatedImage
                    src={selectedMedia.file_url}
                    alt={selectedMedia.original_filename}
                    className="max-w-full max-h-full object-contain"
                    clientId={currentClient?.id}
                  />
                ) : selectedMedia.mime_type?.startsWith('video') ? (
                  <video
                    src={selectedMedia.file_url}
                    controls
                    className="max-w-full max-h-full"
                  >
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <div className="flex flex-col items-center justify-center p-8 text-center">
                    <Image className="h-16 w-16 text-gray-400 mb-4" />
                    <p className="text-lg font-medium text-gray-900 dark:text-white">
                      {selectedMedia.original_filename}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      File type: {selectedMedia.mime_type}
                    </p>
                    <Button
                      onClick={() => window.open(selectedMedia.file_url, '_blank')}
                      className="mt-4"
                    >
                      Open File
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Invite User Modal */}
      <InviteUserModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSuccess={() => {
          // Optionally refresh any team-related data here
          console.log('Team member invited successfully');
        }}
      />

      {/* Instagram Requirements Modal */}
      <InstagramRequirementsModal
        isOpen={showInstagramModal}
        onClose={() => setShowInstagramModal(false)}
        onProceed={proceedWithInstagramConnection}
      />

    </div>
  );
};

export default DashboardPage;